import React, { useState, useEffect } from 'react';
import type { MoFamily } from './types';
import { formatDate } from '../../utils/datetime';

interface SettingsTabProps {
 selectedFamily: MoFamily;
 onFamilyUpdated: (familyHash: string) => void;
 onNavigate: (path: string) => void;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({
 selectedFamily,
 onFamilyUpdated,
 onNavigate,
}) => {
 const [editForm, setEditForm] = useState({ name: '', description: '' });
 const [isEditingInfo, setIsEditingInfo] = useState(false);

 useEffect(() => {
 setEditForm({
 name: selectedFamily.name || '',
 description: selectedFamily.description || '',
 });
 setIsEditingInfo(false);
 }, [selectedFamily]);

 const updateFamilyInfo = async () => {
 try {
 const response = await fetch(`/mofamily/${selectedFamily.moFamilyHash}`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({
 name: editForm.name,
 description: editForm.description,
 }),
 });

 if (response.ok) {
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 shadow-lg z-50';
 notification.textContent = '모패밀리 정보가 수정되었습니다';
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 2000);

 await onFamilyUpdated(selectedFamily.moFamilyHash);
 setIsEditingInfo(false);
 } else {
 const error = await response.json();
 alert(error.error || '모패밀리 정보 수정에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error updating family info:', error);
 alert('모패밀리 정보 수정에 실패했습니다.');
 }
 };

 const deleteFamily = async () => {
 if (!confirm('정말로 이 모패밀리를 삭제하시겠습니까?')) return;

 try {
 const response = await fetch(`/mofamily/${selectedFamily.moFamilyHash}`, {
 method: 'DELETE',
 credentials: 'include',
 });

 if (response.ok) {
 onNavigate('/mofamily');
 } else {
 const error = await response.json();
 alert(error.error || '삭제에 실패했습니다.');
 }
 } catch (error) {
 alert('삭제 중 오류가 발생했습니다.');
 }
 };

 const leaveFamily = async () => {
 if (!confirm('정말로 이 모패밀리에서 탈퇴하시겠습니까?')) return;

 try {
 const response = await fetch(`/mofamily/${selectedFamily.moFamilyHash}/leave`, {
 method: 'POST',
 credentials: 'include',
 });

 if (response.ok) {
 onNavigate('/mofamily');
 } else {
 const error = await response.json();
 alert(error.error || '탈퇴에 실패했습니다.');
 }
 } catch (error) {
 alert('탈퇴 중 오류가 발생했습니다.');
 }
 };

 return (
 <div className="space-y-4">
 {/* 모패밀리 정보 수정 */}
 {selectedFamily.isOwner && (
 <div className="bg-white p-4 border border-gray-200">
 <div className="flex justify-between items-center mb-4">
 <h4 className="text-sm font-medium">모패밀리 정보 수정</h4>
 {!isEditingInfo ? (
 <button
 onClick={() => setIsEditingInfo(true)}
 className="text-blue-600 hover:text-blue-800 text-sm"
 >
 수정
 </button>
 ) : (
 <div className="flex gap-2">
 <button
 onClick={() => {
 setEditForm({
 name: selectedFamily.name || '',
 description: selectedFamily.description || '',
 });
 setIsEditingInfo(false);
 }}
 className="text-gray-600 hover:text-gray-800 text-sm"
 >
 취소
 </button>
 <button
 onClick={updateFamilyInfo}
 className="text-blue-600 hover:text-blue-800 text-sm"
 >
 저장
 </button>
 </div>
 )}
 </div>

 {!isEditingInfo ? (
 <div className="space-y-2">
 <div>
 <label className="text-xs text-gray-500">이름</label>
 <p className="text-sm">{selectedFamily.name}</p>
 </div>
 <div>
 <label className="text-xs text-gray-500">설명</label>
 <p className="text-sm">{selectedFamily.description || '설명이 없습니다.'}</p>
 </div>
 </div>
 ) : (
 <div className="space-y-3">
 <div>
 <label className="block text-xs text-gray-500 mb-1">이름</label>
 <input
 type="text"
 value={editForm.name}
 onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
 placeholder="모패밀리 이름"
 />
 </div>
 <div>
 <label className="block text-xs text-gray-500 mb-1">설명</label>
 <textarea
 value={editForm.description}
 onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
 placeholder="모패밀리 설명"
 rows={3}
 />
 </div>
 </div>
 )}
 </div>
 )}

 {/* 기본 정보 */}
 <div className="bg-gray-50 p-4">
 <h4 className="text-sm font-medium mb-2">기본 정보</h4>
 <p className="text-sm text-gray-600">
 생성일: {formatDate(selectedFamily.createdAt)}
 </p>
 <p className="text-sm text-gray-600">
 하위 모패밀리: {selectedFamily.collaboratingCount || 0}개
 </p>
 <p className="text-sm text-gray-600">
 소유자: {selectedFamily.ownerName || '알 수 없음'}
 </p>
 <p className="text-sm text-gray-600">
 내 역할: {selectedFamily.role === 'owner' ? '소유자' : selectedFamily.role || '멤버'}
 </p>
 </div>

 {selectedFamily.role === 'owner' ? (
 <button
 onClick={deleteFamily}
 className="w-full bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2"
 >
 모패밀리 삭제
 </button>
 ) : (
 <button
 onClick={leaveFamily}
 className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2"
 >
 모패밀리 탈퇴
 </button>
 )}
 </div>
 );
};
